import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import "./Freight.css";
import AlertModal from "../AlertModal/AlertModal";
import config from "../../config";
import useAuth from "../../Auth/useAuth";
import {FaSearch } from 'react-icons/fa';
const getFreightDataUrl = `${config.BASE_URL}/list-freight`;
const insertFreightDataUrl = `${config.BASE_URL}/add-freight`;
const updateFreightDataUrl = `${config.BASE_URL}/update-freight`;
const deleteFreightDataUrl = `${config.BASE_URL}/delete-freight`;

// approval request URLs
const createRequestUrl = `${config.BASE_URL}/create-request`;

const Freight = () => {
  const [result, setResult] = useState([]);
  const [filteredResult, setFilteredResult] = useState([]);
  const [error, setError] = useState(false);
  const [formData, setFormData] = useState({
    from_destination: "",
    to_destination: "",
    rate: "",
  });
  const [submitMessage, setSubmitMessage] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingData, setEditingData] = useState({});
  const editRef = useRef(null); // Ref for the edit area (row)
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    recordId: null,
  });

  // Search state
  const [searchTerm, setSearchTerm] = useState("");

  // Add modal state
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const [isLoading, setIsLoading] = useState(true);
  const { username, userProfile, setActiveModule } = useAuth();

  // Approval request state
  const [approvalRequest, setApprovalRequest] = useState({
    module: "Freight",
    username: username,
    profile: userProfile,
    action: "",
    args: {},
  });

  // Add modal state
  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'info'
  });

  // Modal handler functions
  const showModal = (title, message, type = 'info') => {
    setModalConfig({
      isOpen: true,
      title,
      message,
      type
    });
  };

  const closeModal = () => {
    setModalConfig(prev => ({ ...prev, isOpen: false }));
  };

  // Add modal handlers
  const openAddModal = () => {
    setFormData({
      from_destination: "",
      to_destination: "",
      rate: "",
    });
    setSubmitMessage("");
    setIsAddModalOpen(true);
  };

    const closeAddModal = () => {
    setIsAddModalOpen(false);
    setFormData({
      from_destination: "",
      to_destination: "",
      rate: "",
    });
    setSubmitMessage("");
  };

// Search function
  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);
    
    if (term.trim() === "") {
      setFilteredResult(result);
    } else {
      const filtered = result.filter(item => 
        item.from.toLowerCase().includes(term) || 
        item.to.toLowerCase().includes(term)
      );
      setFilteredResult(filtered);
    }
  };

  // Clear search
  const clearSearch = () => {
    setSearchTerm("");
    setFilteredResult(result);
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    sessionStorage.setItem("actMod", "freight");
    setActiveModule(sessionStorage.getItem("actMod"));
  }, [setActiveModule]);

  useEffect(() => {
  // Update filtered results when main data changes
  if (searchTerm.trim() === "") {
    setFilteredResult(result);
  } else {
    const term = searchTerm.toLowerCase();
    const filtered = result.filter(item => 
      item.from.toLowerCase().includes(term) || 
      item.to.toLowerCase().includes(term)
    );
    setFilteredResult(filtered);
  }
}, [result, searchTerm]); // <-- Added 'searchTerm' to dependencies

  const fetchData = async () => {
    setIsLoading(true);
    try {
      setError(false);
      const response = await axios.get(getFreightDataUrl, {
        withCredentials: true,
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response?.data && Array.isArray(response.data)) {
        console.log("Result Data:", response.data);
        setResult(response.data);
        setFilteredResult(response.data);
      } else {
        setResult([]);
        setFilteredResult([]);
      }
    } catch (error) {
      setError(true);
      console.error("Error while fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const capitalizeWords = (string) => {
    return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const capitalize = capitalizeWords(value);
    setFormData({ ...formData, [name]: capitalize });
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (userProfile === "admin") {
      console.log("Admin user, adding Freight directly.");
      try {
        const response = await axios.post(insertFreightDataUrl, formData, {
          withCredentials: true,
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (response.status === 200) {
          console.log("Freight inserted successfully:", response.data);
          showModal('Success', 'Freight inserted successfully!', 'success');
          fetchData(); // Refresh the data
          closeAddModal(); // Close the modal after successful submission
        } else {
          showModal('Error', 'Failed to insert Freight.', 'error');
        }
      } catch (error) {
        console.error("Error inserting Freight:", error);
        if (error.response) {
          if (error.response.status === 409) {
            showModal('Conflict', 'Freight already exists.', 'error');
          } else if (error.response.status === 404) {
            showModal('Not Found', 'Freight entry not found.', 'error');
          } else {
            showModal('Error', error.response.data || 'Failed to insert Freight.', 'error');
          }
        } else {
          showModal('Network Error', 'Network error or server is down.', 'error');
        }
      }
    } else {
      console.log("User profile is not admin, sending approval request.");
      try {
        const approvalPayload = {
          ...approvalRequest,
          action: "Add",
          args: formData,
        };

        setApprovalRequest(approvalPayload); // Optional: for UI tracking

        const requestApproval = await axios.post(
          createRequestUrl,
          { approvalRequest: approvalPayload },
          {
            withCredentials: true,
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        setApprovalRequest((prev) => ({
          ...prev,
          action: "",
          args: {},
        }));

        if (requestApproval.status === 200) {
          console.log("Freight add - request sent for approval:", formData);
          showModal('Success', 'Freight add - request sent for approval.', 'success');
          closeAddModal(); // Close the modal after successful submission
        } else {
          console.error("Error while submitting request to add freight:", requestApproval);
          showModal('Error', 'Failed to send Freight add request for approval.', 'error');
        }
      } catch (error) {
        console.error("Error while submitting request to add freight:", error);
        showModal('Error', 'Freight add - Error! Trying to create duplicate request.', 'error');
      }

    }
    setTimeout(() => {
      setSubmitMessage("");
    }, 1500);
    setFormData({
      from_destination: "",
      to_destination: "",
      rate: "",
    });
  };

  const handleEdit = (_id) => {
    const shipment = result.find((item) => item._id === _id);
    setEditingId(_id);
    // setEditingData({ ...shipment });
    setEditingData({
      from_destination: shipment.from, // Initialize with previous "from" value
      to_destination: shipment.to, // Initialize with previous "to" value
      rate: Math.round(shipment.rate), // Initialize with previous "rate" value
    });
  };

  const handleEditingChange = (e) => {
    const { name, value } = e.target;
    const capitalize = capitalizeWords(value);
    setEditingData({ ...editingData, [name]: capitalize });
  };

  const handleSave = async (_id) => {
    console.log("Editing Data:", _id);
    const payload = {
      old: result.find((item) => item._id === _id),
      new: editingData,
    };

    if (userProfile === "admin") {
      console.log("Admin user, updating directly.");
      try {
        const response = await axios.post(updateFreightDataUrl, payload, {
          withCredentials: true,
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (response.status === 200) {
          console.log("Freight updated successfully:", response.data);
          showModal('Success', 'Freight updated successfully!', 'success');
          fetchData(); // Refresh the data
        } else {
          showModal('Error', 'Failed to update Freight.', 'error');
        }
        setResult((prev) => [...prev]);
        setEditingId(null);
        setEditingData({});
      } catch (error) {
        console.error("Error updating row:", error);
        setResult((prev) => [...prev]);
        setEditingId(null);
        setEditingData({});

        if (error.response) {
          if (error.response.status === 409) {
            showModal('Conflict', 'Freight already exists.', 'error');
          } else if (error.response.status === 404) {
            showModal('Not Found', 'Freight entry not found.', 'error');
          } else {
            showModal('Error', error.response.data || 'Failed to update row.', 'error');
          }
        } else {
          showModal('Network Error', 'Network error or server is down.', 'error');
        }
      }
    } else {
      console.log(
        "User profile is not admin or accountant, sending approval request."
      );

      const finalApprovalRequest = {
        ...approvalRequest,
        action: "Update",
        args: payload,
      };

      setApprovalRequest(finalApprovalRequest);

      try {
        const requestApproval = await axios.post(
          createRequestUrl,
          {
            approvalRequest: finalApprovalRequest,
          },
          {
            withCredentials: true,
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
        console.log("Approval request created:", requestApproval.data);

        if (requestApproval.status !== 200) {
          showModal('Error', 'Failed to send request for freight update.', 'error');
        }
        console.log("Freight updated -  request sent for approval", _id);
        showModal('Success', 'Freight update - approval request created.', 'success');
        fetchData();
      } catch (error) {
        console.error(
          "Error while submitting request to update freight:",
          error
        );
        showModal('Error', 'Freight - Error! Trying to create duplicate request.', 'error');
      }
    }
    setEditingId(null);
    setApprovalRequest((prev) => ({
      ...prev,
      action: "",
      args: {},
    }));
    setEditingData({});
  };

  const handleDelete = async (id) => {
    const shipmentToDelete = result.find((shipment) => shipment._id === id);

    if (shipmentToDelete) {
      setDeleteModal({
        isOpen: true,
        recordId: id,
        from: shipmentToDelete.from,
        to: shipmentToDelete.to,
        rate: shipmentToDelete.rate,
      });
    }
  };

  const confirmDelete = async () => {
    console.log("Admin user, deleting freight directly.");
    if (userProfile === "admin") {
      try {
        const response = await axios.delete(
          `${deleteFreightDataUrl}/${deleteModal.recordId}`,
          {
            withCredentials: true,
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
        // setResult((prev) => prev.filter((item) => item._id !== deleteModal.recordId));
        if (response.status === 200) {
          console.log("Freight deleted successfully:", deleteModal.recordId);
          showModal('Success', 'Freight deleted successfully!', 'success');
          fetchData(); // Refresh the data
        } else {
          showModal('Error', 'Failed to delete Freight.', 'error');
        }
      } catch (error) {
        console.error("Error deleting Freight:", error);

        let errorMessage = "Error deleting Freight";
        if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }
        showModal('Error', errorMessage, 'error');
      }
    } else {
      console.log("User profile is not admin or accountant, sending approval request.", deleteModal.recordId);
      const freight = result.find(
        (shipment) => shipment._id === deleteModal.recordId
      );
      console.log("Shipment to delete:", freight);
      if (!freight) {
        console.error("Shipment to delete not found.");
        return;
      }
      const approvalPayload = {
        ...approvalRequest,
        action: "Delete",
        args: {
          id: deleteModal.recordId,
          from: freight.from,
          to: freight.to,
          rate: freight.rate,
        },
      };
      try {
        const requestApproval = await axios.post(
          createRequestUrl,
          {
            approvalRequest: approvalPayload,
          },
          {
            withCredentials: true,
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
        console.log("Delete freight -  request sent for approval.", requestApproval.data);
        setApprovalRequest((prev) => ({
          ...prev,
          action: "",
          args: {},
        }));
        if (requestApproval.status === 200) {
          showModal('Success', 'Freight delete - approval request created.', 'success');
        } else {
          showModal('Error', 'Failed to send Freight delete request for approval.', 'error');
        }
        setDeleteModal({ isOpen: false, recordId: null });
      } catch (error) {
        console.error("Error creating approval request:", error);
        showModal('Error', 'Freight delete - Error! Trying to create duplicate request.', 'error');
      }
    }
    setDeleteModal({ isOpen: false, recordId: null });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if the click is outside the current edit row
      if (editRef.current && !editRef.current.contains(event.target)) {
        console.log("Clicked outside, resetting edit mode.");
        setEditingId(null);
        setEditingData({});
      }
    };

    // Attach the event listener
    document.addEventListener("mousedown", handleClickOutside);

    // Cleanup the event listener
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div id="freight">
      {isLoading && (
        <div className="spinner-container">
          <div className="spinner"></div>
          <span>Loading Freight...</span>
        </div>
      )}
      <div className="container-freight">
        <AlertModal
          isOpen={modalConfig.isOpen}
          onClose={closeModal}
          title={modalConfig.title}
          message={modalConfig.message}
          type={modalConfig.type}
        />

        
        {/* Card Header with Add Button - Similar to Opportunity */}
        {!isLoading && (
          <div className="card-header">
            {/* <h2>Freight</h2> */}
            <div className="freight-search-container">
              <FaSearch className="search-icon" />
              <input
                type="text"
                className="search-input"
                placeholder="Search by From or To destination... (e.g., Pune, Mumbai, Delhi)"
                value={searchTerm}
                onChange={handleSearch}
              />
              {searchTerm && (
                <button className="clear-search" onClick={clearSearch}>
                  ×
                </button>
              )}
            </div>
            <button className="add-button" onClick={openAddModal}>
              <span className="plus-icon">+</span> Add Freight
            </button>
          </div>
        )}

        {/* Add Freight Modal */}
        {isAddModalOpen && (
          <div className="modal-overlay" onClick={closeAddModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Add New Freight</h3>
                <button className="close-button" onClick={closeAddModal}>×</button>
              </div>
              <form onSubmit={handleFormSubmit} autoComplete="off">
                <div className="modal-body">
                  <div className="form-group">
                    <label htmlFor="from_destination" className="required">From Destination:</label>
                    <input
                      type="text"
                      id="from_destination"
                      name="from_destination"
                      value={formData.from_destination}
                      onChange={handleInputChange}
                      placeholder="Enter origin location"
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="to_destination" className="required">To Destination:</label>
                    <input
                      type="text"
                      id="to_destination"
                      name="to_destination"
                      value={formData.to_destination}
                      onChange={handleInputChange}
                      placeholder="Enter destination location"
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="rate" className="required">Rate:</label>
                    <input
                      type="number"
                      id="rate"
                      name="rate"
                      value={formData.rate}
                      onChange={handleInputChange}
                      placeholder="Enter rate amount"
                      min="0"
                      step="0.01"
                      required
                    />
                  </div>
                  
                  {submitMessage && <p className="submit-message">{submitMessage}</p>}
                </div>
                
                <div className="modal-footer">
                  <button type="button" className="freight-cancel-button" onClick={closeAddModal}>
                    Cancel
                  </button>
                  <button type="submit" className="submit-button">
                    Submit
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Search Bar Section */}
        {/* {!isLoading && (
          <div className="search-section">
            <div className="freight-search-container">
              <FaSearch className="search-icon" />
              <input
                type="text"
                className="search-input"
                placeholder="Search by From or To destination... (e.g., Pune, Mumbai, Delhi)"
                value={searchTerm}
                onChange={handleSearch}
              />
              {searchTerm && (
                <button className="clear-search" onClick={clearSearch}>
                  ×
                </button>
              )}
            </div>
            {searchTerm && (
              <div className="search-results-info">
                Found <strong>{filteredResult.length}</strong> result{filteredResult.length !== 1 ? 's' : ''} for "<strong>{searchTerm}</strong>"
              </div>
            )}
          </div>
        )} */}

        <div className="table-wrapper" id="results">
          {/* <h2>Current Rates</h2> */}
          {isLoading && (
            <div className="modal-spinner-overlay">
              <div className="spinner"></div>
              <span>Loading freight data...</span>
            </div>
          )}
          {!isLoading && (
            <div className="scrollable-table">
              <table id="resultsTable">
                <thead>
                  <tr>
                    {/* <th>Index</th> */}
                    <th>From Destination</th>
                    <th>To Destination</th>
                    <th>Rate</th>
                    <th>Edit</th>
                    <th>Delete</th>
                  </tr>
                </thead>
                <tbody>
                  {error ? (
                    <tr>
                      <td colSpan="5">
                        Error fetching data. Please try again later.
                      </td>
                    </tr>
                  ) : filteredResult.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="empty-row">
                        No freight data available.
                      </td>
                    </tr>
                  ) : (
                    filteredResult.map((shipment) => (
                      <tr
                        key={shipment._id}
                        ref={editingId === shipment._id ? editRef : null}
                        className={editingId === shipment._id ? "editing" : ""}
                      >
                        {/* <td>{shipment.id}</td> */}
                        <td>
                          {editingId === shipment._id ? (
                            <input
                              className="editInput"
                              type="text"
                              name="from_destination"
                              value={editingData.from_destination || ""}
                              onChange={handleEditingChange}
                            />
                          ) : (
                            shipment.from
                          )}
                        </td>

                        <td>
                          {editingId === shipment._id ? (
                            <input
                              className="editInput"
                              type="text"
                              name="to_destination"
                              value={editingData.to_destination || ""}
                              onChange={handleEditingChange}
                            />
                          ) : (
                            shipment.to
                          )}
                        </td>

                        <td>
                          {editingId === shipment._id ? (
                            <input
                              className="editInput"
                              type="number"
                              name="rate"
                              value={editingData.rate || ""}
                              onChange={handleEditingChange}
                            />
                          ) : (
                            shipment.rate
                          )}
                        </td>

                        <td>
                          {editingId === shipment._id ? (
                            <button
                              className="save-btn"
                              onClick={() => handleSave(shipment._id)}
                            >
                              Save
                            </button>
                          ) : (
                            <button
                              className="edit-btn"
                              onClick={() => handleEdit(shipment._id)}
                            >
                              Edit
                            </button>
                          )}
                        </td>

                        <td>
                          <button
                            className="delete-btn"
                            onClick={() => handleDelete(shipment._id)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
          {deleteModal.isOpen && (
            <div className="delete-modal-overlay">
              <div className="delete-modal">
                <div className="delete-modal-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    width="54"
                    height="54"
                  >
                    <path fill="none" d="M0 0h24v24H0z" />
                    <path
                      fill="#ff4757"
                      d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm0-11.414L9.172 7.757 7.757 9.172 10.586 12l-2.829 2.828 1.415 1.415L12 13.414l2.828 2.829 1.415-1.415L13.414 12l2.829-2.828-1.415-1.415L12 10.586z"
                    />
                  </svg>
                </div>
                <h3>Confirm Delete</h3>
                <p>
                  Are you sure you want to delete this freight?
                  <span className="trip-id-highlight"></span>
                  <br></br>
                  <b>From</b>: {deleteModal.from}, <b>To</b>: {deleteModal.to},{" "}
                  <b>Rate</b>: {deleteModal.rate}
                </p>
                <p className="warning-text">This action cannot be undone.</p>
                <div className="delete-modal-buttons">
                  <button
                    className="cancel-btn"
                    onClick={() =>
                      setDeleteModal({ isOpen: false, recordId: null })
                    }
                  >
                    Cancel
                  </button>
                  <button
                    className="confirm-delete-btn"
                    onClick={confirmDelete}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Freight;